import { useEffect, useRef, useState } from 'react';
import { DragIndicatorIcon, HFBox, HFButton, HFChip, HFThemeProvider, hf, hfAlpha, useHfTheme, type HfThemeMode } from '@hf/designSystem/v1';
import { DemoShell, SHELL_HEIGHT, SHELL_WIDTH } from './DemoShell';
import { INITIAL_DEMO_STATE, stageStartState, TOUR, type DemoPatch, type DemoState, type TourDriver } from './demoTour';
import { LANDING_STAGES, type LandingStageId } from './stages';

type CursorState = { x: number; y: number; ms: number; pressed: boolean; clicks: number };
type Curtain = 'restart' | 'seek' | null;

const TICK_MS = 50;
/** Playback speed for every scripted wait and cursor move (< 1 is faster). */
const TEMPO = 0.8;
const CURSOR_HOME = { x: 220, y: 140 };
const MIN_SCALE = 0.45;
/** Space kept under the frame so the landing never needs a vertical scrollbar. */
const BOTTOM_GUTTER_PX = 24;

/** Shared by the shell (rendered inside this subtree), cursor, and ghost. */
const DEMO_KEYFRAMES = {
  '@keyframes hfDemoIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
  '@keyframes hfDemoFade': { from: { opacity: 0 }, to: { opacity: 1 } },
  '@keyframes hfDemoPop': { '0%': { transform: 'scale(0.97)' }, '55%': { transform: 'scale(1.015)' }, '100%': { transform: 'none' } },
  '@keyframes hfDemoMenu': { from: { opacity: 0, transform: 'scale(0.96) translateY(-4px)' }, to: { opacity: 1, transform: 'none' } },
  '@keyframes hfDemoToast': { from: { opacity: 0, transform: 'translateY(12px)' }, to: { opacity: 1, transform: 'none' } },
  '@keyframes hfDemoDraw': { from: { strokeDashoffset: 1 }, to: { strokeDashoffset: 0 } },
  '@keyframes hfDemoPage': { from: { opacity: 0, transform: 'translateY(16px) rotate(-2deg)' }, to: { opacity: 1, transform: 'none' } },
  '@keyframes hfDemoCaret': { '0%': { opacity: 1 }, '50%': { opacity: 0 } },
  '@keyframes hfDemoRipple': { from: { transform: 'scale(0.3)', opacity: 0.8 }, to: { transform: 'scale(2.2)', opacity: 0 } },
  '@keyframes hfDemoBar': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
  '@keyframes hfDemoSpin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(-360deg)' } },
};

function abortError() {
  return new DOMException('Aborted', 'AbortError');
}

function isAbort(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

function DemoCursor({ cursor, ghost }: { cursor: CursorState; ghost: string | null }) {
  return (
    <HFBox
      aria-hidden
      data-testid="demo-cursor"
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 20,
        pointerEvents: 'none',
        transform: `translate(${cursor.x}px, ${cursor.y}px)`,
        transition: `transform ${cursor.ms}ms cubic-bezier(0.45, 0, 0.2, 1)`,
      }}
    >
      {ghost ? (
        <HFBox
          sx={{
            position: 'absolute',
            left: 16,
            top: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            border: 2,
            borderColor: 'primary.main',
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 8,
            fontSize: 12,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            transform: 'rotate(-3deg)',
            animation: 'hfDemoFade 0.2s ease both',
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
          {ghost}
        </HFBox>
      ) : null}
      {cursor.clicks > 0 ? (
        <HFBox
          key={cursor.clicks}
          sx={{
            position: 'absolute',
            left: -14,
            top: -14,
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: 2,
            borderColor: 'primary.main',
            opacity: 0,
            animation: 'hfDemoRipple 0.5s ease-out',
          }}
        />
      ) : null}
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        style={{
          position: 'absolute',
          left: -5,
          top: -3,
          transform: `scale(${cursor.pressed ? 0.85 : 1})`,
          transformOrigin: '5px 3px',
          transition: 'transform 0.12s ease',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))',
        }}
      >
        <path
          d="M5.5 3.5L19 12.2l-6.1 1.4 2.7 6.6-2.4 1-2.8-6.7L5.5 20.5V3.5z"
          fill={hf.navy700}
          stroke={hf.white}
          strokeWidth="1.25"
          strokeLinejoin="round"
        />
      </svg>
    </HFBox>
  );
}

function RestartCurtain() {
  return (
    <HFBox
      role="status"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 25,
        display: 'grid',
        placeItems: 'center',
        bgcolor: hfAlpha(hf.navy900, 0.55),
        animation: 'hfDemoFade 0.35s ease both',
      }}
    >
      <HFBox
        sx={{
          width: 320,
          px: 3,
          py: 2.5,
          borderRadius: 2,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 12,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          animation: 'hfDemoMenu 0.35s ease both',
        }}
      >
        <HFBox aria-hidden sx={{ fontSize: 28, lineHeight: 1, color: 'primary.main', animation: 'hfDemoSpin 1.2s linear infinite' }}>
          ↻
        </HFBox>
        <HFBox sx={{ fontSize: 16, fontWeight: 800 }}>Replaying the tour</HFBox>
        <HFBox sx={{ fontSize: 13, color: 'text.secondary' }}>Starting again from the fund board</HFBox>
        <HFBox sx={{ width: '100%', height: 4, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <HFBox sx={{ height: '100%', bgcolor: 'primary.main', transformOrigin: 'left', animation: 'hfDemoBar 1.5s linear both' }} />
        </HFBox>
      </HFBox>
    </HFBox>
  );
}

const CAPTION_SIDEBAR_PX = 260;

function DemoChapterNav({
  stageId,
  demoTheme,
  playing,
  onSeek,
  onToggleTheme,
  onTogglePlay,
}: {
  stageId: LandingStageId;
  demoTheme: HfThemeMode;
  playing: boolean;
  onSeek: (index: number) => void;
  onToggleTheme: () => void;
  onTogglePlay: () => void;
}) {
  const stageIndex = Math.max(
    0,
    LANDING_STAGES.findIndex((s) => s.id === stageId),
  );
  return (
    <HFBox component="nav" aria-label="Tour chapters" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
      {LANDING_STAGES.map((s, index) => (
        <HFChip
          key={s.id}
          label={s.title}
          size="small"
          clickable
          onClick={() => onSeek(index)}
          aria-current={s.id === stageId ? 'step' : undefined}
          color={s.id === stageId ? 'primary' : 'default'}
          variant={s.id === stageId ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600, opacity: index < stageIndex ? 0.7 : 1 }}
        />
      ))}
      <HFBox sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
        <HFButton size="small" variant="outlined" onClick={onToggleTheme}>
          Demo {demoTheme === 'dark' ? 'light' : 'dark'}
        </HFButton>
        <HFButton size="small" variant="outlined" onClick={onTogglePlay}>
          {playing ? 'Pause' : 'Play'}
        </HFButton>
      </HFBox>
    </HFBox>
  );
}

/** Chapter caption and live subtitle. Docks beside the frame so the animation can grow taller. */
function DemoCaptionSidebar({
  stageId,
  subtitle,
  playing,
  restarting,
}: {
  stageId: LandingStageId;
  subtitle: string;
  playing: boolean;
  restarting: boolean;
}) {
  const stageIndex = Math.max(
    0,
    LANDING_STAGES.findIndex((s) => s.id === stageId),
  );
  const stage = LANDING_STAGES[stageIndex]!;
  return (
    <HFBox
      sx={{
        width: { xs: '100%', md: CAPTION_SIDEBAR_PX },
        flex: { xs: '1 1 auto', md: `0 0 ${CAPTION_SIDEBAR_PX}px` },
        px: 2,
        py: 1.75,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        alignSelf: { md: 'stretch' },
        minHeight: { md: 0 },
      }}
    >
      <HFBox
        component="p"
        sx={{ m: 0, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'primary.main' }}
      >
        {restarting ? 'Replaying' : `Chapter ${stageIndex + 1} of ${LANDING_STAGES.length}`}
      </HFBox>
      <HFBox component="h2" sx={{ m: 0, fontSize: '1.05rem', fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
        {restarting ? 'Starting over' : stage.title}
      </HFBox>
      <HFBox component="p" sx={{ m: 0, fontSize: '0.875rem', color: 'text.secondary', lineHeight: 1.5 }}>
        {restarting ? 'The tour loops. Pick any chapter above to jump to it.' : stage.caption}
      </HFBox>
      <HFBox
        key={subtitle || 'idle'}
        component="p"
        aria-live="polite"
        sx={{
          m: 0,
          mt: 'auto',
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          minHeight: '3.2em',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'primary.main',
          lineHeight: 1.45,
          animation: 'hfDemoFade 0.3s ease both',
        }}
      >
        {subtitle ? (
          <>
            <HFBox
              component="span"
              aria-hidden
              sx={{
                mt: '0.35em',
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: playing ? 'primary.main' : 'text.disabled',
                flex: '0 0 auto',
              }}
            />
            {subtitle}
          </>
        ) : null}
      </HFBox>
    </HFBox>
  );
}

/**
 * Scripted product video for the non-subscriber landing: a purpose-built Tearsheets shell painted
 * from seed data, walked by an animated cursor. The nested theme paints the whole demo light or dark.
 */
export function WorkflowDemo() {
  const appTheme = useHfTheme();
  const [demoTheme, setDemoTheme] = useState<HfThemeMode>(appTheme.palette.mode === 'dark' ? 'dark' : 'light');
  const [playing, setPlaying] = useState(true);
  const [stageId, setStageId] = useState<LandingStageId>('reports');
  const [subtitle, setSubtitle] = useState('');
  const [state, setState] = useState<DemoState>(INITIAL_DEMO_STATE);
  const [cursor, setCursor] = useState<CursorState>({ ...CURSOR_HOME, ms: 0, pressed: false, clicks: 0 });
  const [ghost, setGhost] = useState<string | null>(null);
  const [curtain, setCurtain] = useState<Curtain>(null);
  const [scale, setScale] = useState(0.8);

  const wrapRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const seekRef = useRef<(index: number) => void>(() => undefined);
  pausedRef.current = !playing;

  useEffect(() => {
    setDemoTheme(appTheme.palette.mode === 'dark' ? 'dark' : 'light');
  }, [appTheme.palette.mode]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measure = () => {
      if (wrap.clientWidth <= 0) return;
      const top = wrap.getBoundingClientRect().top + window.scrollY;
      const byHeight = (window.innerHeight - top - BOTTOM_GUTTER_PX) / SHELL_HEIGHT;
      setScale(Math.max(MIN_SCALE, Math.min(wrap.clientWidth / SHELL_WIDTH, byHeight)));
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(wrap);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [state.view]);

  useEffect(() => {
    let controller: AbortController | null = null;

    const start = (from: number) => {
      controller?.abort();
      const ac = new AbortController();
      controller = ac;
      const { signal } = ac;

      const wait = async (ms: number) => {
        let left = ms * TEMPO;
        do {
          if (signal.aborted) throw abortError();
          const started = performance.now();
          await new Promise((resolve) => window.setTimeout(resolve, TICK_MS));
          if (signal.aborted) throw abortError();
          // A late timer still has to advance the script, or a busy runner stretches every beat.
          if (!pausedRef.current) left -= Math.max(TICK_MS, performance.now() - started);
        } while (left > 0);
      };
      const set = (patch: DemoPatch) => setState((s) => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) }));
      const find = (target: string) => shellRef.current?.querySelector<HTMLElement>(`[data-demo="${target}"]`) ?? null;
      const pointOf = (target: string) => {
        const frame = frameRef.current;
        const el = find(target);
        if (!frame || !el) return null;
        const box = frame.getBoundingClientRect();
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return null;
        return { x: rect.left - box.left + Math.min(rect.width / 2, 60), y: rect.top - box.top + Math.min(rect.height / 2, 24) };
      };
      const move = async (target: string, ms = 750) => {
        const point = pointOf(target);
        if (point) setCursor((c) => ({ ...c, ...point, ms: ms * TEMPO }));
        await wait(ms + 100);
      };
      const press = async () => {
        setCursor((c) => ({ ...c, pressed: true, clicks: c.clicks + 1 }));
        await wait(150);
      };
      const release = () => setCursor((c) => ({ ...c, pressed: false }));

      const driver: TourDriver = {
        say: setSubtitle,
        set,
        wait,
        move,
        click: async (target, patch) => {
          await move(target);
          await press();
          if (patch) set(patch);
          release();
          await wait(250);
        },
        drag: async (from, to, label, steps) => {
          await move(from);
          await press();
          await wait(350);
          setGhost(label);
          set(steps.grab);
          await wait(500);
          await move(to, 2200);
          set(steps.hover);
          await wait(1000);
          set(steps.drop);
          setGhost(null);
          release();
          await wait(400);
        },
        type: async (_target, text, apply) => {
          for (let i = 1; i <= text.length; i += 1) {
            set(apply(text.slice(0, i)));
            await wait(i % 4 === 0 ? 100 : 50);
          }
        },
        reveal: async (target) => {
          const scroller = scrollRef.current;
          const el = target ? find(target) : null;
          let top = 0;
          if (scroller && el) {
            const box = scroller.getBoundingClientRect();
            const rect = el.getBoundingClientRect();
            const k = scroller.offsetHeight > 0 ? box.height / scroller.offsetHeight : 1;
            top = Math.max(0, scroller.scrollTop + (rect.top - box.top) / (k || 1) - 60);
          }
          if (scroller) {
            if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top, behavior: 'smooth' });
            else scroller.scrollTop = top;
          }
          await wait(800);
        },
      };

      void (async () => {
        try {
          let index = from;
          setGhost(null);
          if (from > 0) {
            setCurtain('seek');
            await wait(250);
          }
          setState(stageStartState(index));
          setCurtain(null);
          for (;;) {
            for (; index < TOUR.length; index += 1) {
              setStageId(TOUR[index]!.id);
              setSubtitle('');
              await TOUR[index]!.run(driver);
            }
            setSubtitle('');
            setCurtain('restart');
            await wait(1900);
            index = 0;
            setState(INITIAL_DEMO_STATE);
            setStageId(TOUR[0]!.id);
            setGhost(null);
            setCursor((c) => ({ ...c, ...CURSOR_HOME, ms: 0 }));
            await wait(300);
            setCurtain(null);
          }
        } catch (error) {
          if (!isAbort(error)) console.warn('[tearsheets landing tour]', error);
        }
      })();
    };

    seekRef.current = start;
    start(0);
    return () => controller?.abort();
  }, []);

  const shellWidth = SHELL_WIDTH * scale;

  return (
    <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, width: '100%', ...DEMO_KEYFRAMES }}>
      <DemoChapterNav
        stageId={stageId}
        demoTheme={demoTheme}
        playing={playing}
        onSeek={(index) => {
          setPlaying(true);
          seekRef.current(index);
        }}
        onToggleTheme={() => setDemoTheme((mode) => (mode === 'dark' ? 'light' : 'dark'))}
        onTogglePlay={() => setPlaying((p) => !p)}
      />

      <HFBox
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: 1.5,
          width: '100%',
        }}
      >
        <HFBox ref={wrapRef} sx={{ flex: '1 1 auto', minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
          <HFThemeProvider mode={demoTheme} onModeChange={setDemoTheme} persist={false} baseline={false}>
            <HFBox
              ref={frameRef}
              role="region"
              aria-label="Animated Tearsheets product tour"
              sx={{
                position: 'relative',
                width: shellWidth,
                height: SHELL_HEIGHT * scale,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'background.default',
                boxShadow: (t) => (t.palette.mode === 'dark' ? '0 24px 64px rgba(0,0,0,0.45)' : '0 24px 64px rgba(15,23,41,0.12)'),
              }}
            >
              <HFBox
                ref={shellRef}
                sx={{
                  width: SHELL_WIDTH,
                  height: SHELL_HEIGHT,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  opacity: curtain === 'seek' ? 0.3 : 1,
                  filter: curtain === 'restart' ? 'blur(2px) saturate(0.6)' : 'none',
                  transition: 'opacity 0.25s ease, filter 0.35s ease',
                }}
              >
                <DemoShell state={state} scrollRef={scrollRef} />
              </HFBox>
              <DemoCursor cursor={cursor} ghost={ghost} />
              {curtain === 'restart' ? <RestartCurtain /> : null}
            </HFBox>
          </HFThemeProvider>
        </HFBox>

        <DemoCaptionSidebar stageId={stageId} subtitle={subtitle} playing={playing} restarting={curtain === 'restart'} />
      </HFBox>
    </HFBox>
  );
}
